<template>
  <div id="app">
    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="container">
        <!-- 汉堡菜单按钮 -->
        <div class="hamburger" @click="toggleMenu">
          <span></span>
          <span></span>
          <span></span>
        </div>

        <!-- 左侧空占位 -->
        <div class="nav-left"></div>

        <!-- 中间导航链接 -->
        <ul class="nav-links" :class="{ 'active': isMenuOpen }" @click="handleNavLinkClick">
          <li><a href="#about">{{ $t('nav_about') }}</a></li>
          <li><a href="#features">{{ $t('nav_benefits') }}</a></li>
          <li class="logo"><a href="#home">Herlinde</a></li>
          <li><a href="#certificates">{{ $t('nav_certificates') }}</a></li>
          <li><a href="#contact">{{ $t('nav_contacts') }}</a></li>
        </ul>

        <!-- 右侧操作区（语言切换） -->
        <div class="nav-actions">
          <div class="language-switch">
            <ChangeLanguage></ChangeLanguage>
          </div>
        </div>
      </div>
    </nav>

    <!-- 英雄区域 -->
    <section id="home" class="hero">
      <canvas id="mist-canvas"></canvas>
      <div class="hero-content">
        <h2>{{ $t('hero_welcome') }}</h2>
        <p>{{ $t('hero_subtitle') }}</p>
        <div class="cta-buttons">
          <button class="btn primary" @click="scrollToSection('kangertech-products')">{{ $t('hero_new_product') }}</button>
          <button class="btn secondary" @click="showProductCenter = true">{{ $t('hero_all_product') }}</button>
          <a href="https://pay.herlinde-int.de" target="_blank" class="btn secondary">{{ $t('hero_go_to_payment') }}</a>
        </div>
      </div>
      <div class="hero-image">
        <div class="hero-bg"></div>
      </div>
    </section>

    <!-- Kangertech Products -->
    <section id="kangertech-products" class="products">
      <div class="container">
        <h2 class="section-title">{{ $t('products_kangertech') }}</h2>
        <div class="products-grid">
          <div class="product-item" v-for="product in kangertechProducts" :key="product.id">
            <div class="product-image">
              <div class="product-bg" :style="{ backgroundImage: 'url(' + product.img + ')' }"></div>
            </div>
            <div class="product-info">
              <h3>{{ product.name }}</h3>
              <p>{{ $t(product.description) }}</p>
              <button class="btn small" @click="showProductCenter = true">{{ $t('products_learn_more') }}</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- products -->
    <section id="products" class="products">
      <div class="container">
        <h2 class="section-title">{{ $t('products_sopuria') }}</h2>
        <div class="products-grid">
          <div class="product-item" v-for="(product) in sopuriaProducts" :key="product.id">
            <div class="product-image">
              <div class="product-bg" :style="{ backgroundImage: 'url(' + product.img + ')' }"></div>
            </div>
            <div class="product-info">
              <h3>{{ product.name }}</h3>
              <p>{{ $t(product.description) }}</p>
              <button class="btn small" @click="showProductCenter = true">{{ $t('products_learn_more') }}</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 功能介绍 - 跑马灯效果 -->
    <section id="features" class="features-transform">
      <canvas id="features-mist-canvas"></canvas>
      <div class="features-container">
        <!-- 初始文字 -->
        <div class="hero-section">
          <div class="hero-text">
            <h1 :class="{ 'text-transition': textTransition }" ref="heroTitle">{{ currentHeroText.title }}</h1>
            <p :class="{ 'text-transition': textTransition }" ref="heroDesc">{{ currentHeroText.description }}</p>
          </div>
        </div>

        <!-- 跑马灯容器 -->
        <div class="marquee-container">
          <div class="marquee-wrapper" ref="marqueeWrapper">
            <!-- 5张图片的跑马灯 -->
            <div class="marquee-slide" v-for="(item, index) in marqueeImages" :key="index">
              <div class="marquee-image" :style="{ backgroundImage: `url(${item.image})` }"></div>
            </div>
          </div>

          <!-- 左右控制按钮 -->
          <button class="marquee-btn prev" @click="scrollMarquee('prev')">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button class="marquee-btn next" @click="scrollMarquee('next')">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          <!-- 指示器 -->
          <div class="marquee-indicators">
            <span v-for="(image, index) in marqueeImages" :key="index"
                  class="indicator"
                  :class="{ active: currentSlide === index }"
                  @click="goToSlide(index)"></span>
          </div>
        </div>
      </div>
    </section>

    <!-- 关于我们 -->
    <section id="about" class="about">
      <div class="container">
        <div class="about-content">
          <h2 class="section-title">{{ $t('about_title') }}</h2>
          <p class="fade-in-text">{{ $t('about_desc_1') }}</p>
          <p class="fade-in-text">{{ $t('about_desc_2') }}</p>
        </div>
        <div class="about-stats">
          <div class="stat-item">
            <span class="stat-number">{{ stats.clients.current }}+</span>
            <span class="stat-label">{{ $t('about_stats_clients') }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ stats.projects.current }}+</span>
            <span class="stat-label">{{ $t('about_stats_projects') }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ stats.team.current }}+</span>
            <span class="stat-label">{{ $t('about_stats_team') }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ stats.countries.current }}+</span>
            <span class="stat-label">{{ $t('about_stats_countries') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 证书模块（照片墙） -->
    <section id="certificates" class="certificates">
      <div class="container">
        <h2 class="section-title">{{ $t('certificates_title') }}</h2>
        <p class="section-subtitle">{{ $t('certificates_subtitle') }}</p>

        <div class="certificates-grid">
          <!-- 证书项目 -->
          <div class="certificate-item" @click="showHighRes('COC-20230510')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-COC-20230510.jpg" :alt="$t('certificates_coc')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_coc') }}</h3>
              <p>{{ $t('certificates_coc_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('COC-20230718')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-COC-20230718.jpg" :alt="$t('certificates_coc')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_coc') }}</h3>
              <p>{{ $t('certificates_coc_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('MHRA-20231219')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-MHRA-20231219.jpg" :alt="$t('certificates_mhra')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_mhra') }}</h3>
              <p>{{ $t('certificates_mhra_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('MHRA-20240411')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-MHRA-20240411.jpg" :alt="$t('certificates_mhra')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_mhra') }}</h3>
              <p>{{ $t('certificates_mhra_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('MHRA-20241028')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-MHRA-20241028.jpg" :alt="$t('certificates_mhra')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_mhra') }}</h3>
              <p>{{ $t('certificates_mhra_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('RoHS-20240408')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-RoHS-20240408.jpg" alt="RoHS Certificate" />
            </div>
            <div class="certificate-info">
              <h3>RoHS Certificate</h3>
              <p>Restriction of Hazardous Substances</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('RoHS-20240531')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-RoHS-20240531.jpg" alt="RoHS Certificate" />
            </div>
            <div class="certificate-info">
              <h3>RoHS Certificate</h3>
              <p>Restriction of Hazardous Substances</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('RoHS-20241023')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-RoHS-20241023.jpg" alt="RoHS Certificate" />
            </div>
            <div class="certificate-info">
              <h3>RoHS Certificate</h3>
              <p>Restriction of Hazardous Substances</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('RoHS-20241212')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-RoHS-20241212.jpg" alt="RoHS Certificate" />
            </div>
            <div class="certificate-info">
              <h3>RoHS Certificate</h3>
              <p>Restriction of Hazardous Substances</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('TPD-20230721')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-TPD-20230721.jpg" alt="APD Certificate" />
            </div>
            <div class="certificate-info">
              <h3>APD Certificate</h3>
              <p>Atomizer Products Directive</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('TPD-20231219')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-TPD-20231219.jpg" alt="APD Certificate" />
            </div>
            <div class="certificate-info">
              <h3>APD Certificate</h3>
              <p>Atomizer Products Directive</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('TPD-20240411')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-TPD-20240411.jpg" alt="APD Certificate" />
            </div>
            <div class="certificate-info">
              <h3>APD Certificate</h3>
              <p>Atomizer Products Directive</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('TPD-20241028')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-TPD-20241028.jpg" alt="APD Certificate" />
            </div>
            <div class="certificate-info">
              <h3>APD Certificate</h3>
              <p>Atomizer Products Directive</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('VOC-20230309')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-VOC-20230309.jpg" :alt="$t('certificates_voc')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_voc') }}</h3>
              <p>{{ $t('certificates_voc_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('VOC-20241024')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-VOC-20241024.jpg" :alt="$t('certificates_voc')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_voc') }}</h3>
              <p>{{ $t('certificates_voc_desc') }}</p>
            </div>
          </div>

          <div class="certificate-item" @click="showHighRes('VOC-20241218')">
            <div class="certificate-image">
              <img src="@/assets/img/ccompany-honor-VOC-20241218.jpg" :alt="$t('certificates_voc')" />
            </div>
            <div class="certificate-info">
              <h3>{{ $t('certificates_voc') }}</h3>
              <p>{{ $t('certificates_voc_desc') }}</p>
            </div>
          </div>
        </div>

        <!-- 高清证书图片展示 -->
        <div v-if="showHighResImage" class="high-res-certificate" @mouseleave="hideHighRes">
          <div v-if="!highResImageLoaded" class="image-loading">
            <div class="loading-spinner"></div>
          </div>
          <img :src="highResImageUrl" :alt="'High Resolution Certificate'" @load="highResImageLoaded = true" />
        </div>
      </div>
    </section>

    <!-- 联系我们 -->
    <section id="contact" class="contact">
      <div class="container">
        <h2 class="section-title">{{ $t('contact_title') }}</h2>
        <div class="contact-form">
          <!-- 邮箱展示区域 -->
          <div class="email-display">
            <p class="email-subtitle fade-in-text">{{ $t('contact_subtitle') }}</p>
            <p class="email-text fade-in-text">Info@herlinde-int.de</p>
          </div>
          <div class="divider"></div>
          <form id="contact-form" method="POST" action="https://formspree.io/f/xeqbgkng" @submit="handleSubmit">
            <!-- Formspree requires this hidden field to know where to send the email -->
            <input type="hidden" name="_to" value="835152576@qq.com">
            <!-- Prevent spam -->
            <input type="hidden" name="_subject" value="New contact message from website">
            <!-- Success message URL (optional) -->
            <input type="hidden" name="_next" value="#contact?success=true">
          <!--暂时注释掉，不要删除-->
            <!-- <div class="form-group">
              <input type="text" name="name" placeholder="Your Name" required>
            </div>
            <div class="form-group">
              <input type="email" name="email" placeholder="Your Email" required>
            </div>
            <div class="form-group">
              <textarea name="message" placeholder="Your Message" rows="5" required></textarea>
            </div>
            <button type="submit" class="btn primary" :disabled="isSubmitting">
              <span v-if="isSubmitting">Sending...</span>
              <span v-else>Send Message</span>
            </button> -->
            <!-- Success message -->
            <div v-if="showSuccessMessage" class="success-message">
              {{ $t('contact_success_message') }}
            </div>
          </form>
          <div class="contact-info">
            <div class="contact-info-left fade-in-text">
              <p>Solmsstr. 23 60486 Frankfurt am main Germany</p>
              <p>Phone: +49 176 64921162</p>
            </div>
            <div class="contact-info-center fade-in-text">
              <p>&copy; 2023 Herlinde International. All rights reserved.</p>
            </div>
            <div class="contact-info-right fade-in-text">
              <p>Feel free to contact us anytime</p>
              <p>Monday - Friday: 9 AM - 5 PM</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ProductCenter页面 -->
    <div class="product-center-overlay" v-if="showProductCenter">
      <ProductCenter @back-to-home="showProductCenter = false" />
    </div>

    <!-- WhatsApp悬浮按钮 -->
    <a href="https://wa.me/004917641138247" target="_blank" class="whatsapp-float">
      <img src="./assets/img/WhatsApp.png" alt="WhatsApp" class="whatsapp-icon">
    </a>
  </div>
</template>

<script>
import ChangeLanguage from './i18n/change-language.vue';
import ProductCenter from './views/ProductCenter.vue';
// Marquee images
import marquee1 from '@/assets/img/marquee-1.png';
import marquee2 from '@/assets/img/marquee-2.png';
import { allProducts } from '@/data/products.js'

export default {
  name: 'App',
  components: {
    ChangeLanguage,
    ProductCenter
  },
  computed: {
    // 计算属性，获取Sopuria Products的商品列表
    sopuriaProducts() {
      return this.allProducts.filter(product => product.category === 'product_category_sopuria').slice(0, 4);
    },
    // 计算属性，获取Kangertech Products的商品列表
    kangertechProducts() {
      return this.allProducts.filter(product => product.category === 'product_category_kangertech').slice(0, 5);
    }
  },
  data() {
    return {

      // 跑马灯图片和对应的hero-text内容
      marqueeImages: [
        {
          image: marquee1,
          title: this.$t('features_title_1'),
          description: this.$t('features_desc_1')
        },
        {
          image: marquee2,
          title: this.$t('features_title_2'),
          description: this.$t('features_desc_2')
        },
      ],
      // 当前显示的hero-text内容
      currentHeroText: {
        title: this.$t('features_title_1'),
        description: this.$t('features_desc_1')
      },
      currentSlide: 0,
      // 自动播放配置
      autoPlayInterval: 10000, // 自动播放间隔时间（毫秒）
      autoPlayTimer: null, // 自动播放定时器
      // 文本切换特效
      textTransition: false,

      isMenuOpen: false,
      showProductCenter: false,
      showHighResImage: false,
      highResImageUrl: '',
      highResImageLoaded: false,
      highResImagePosition: { x: 0, y: 0 },
      // 统计数字数据
      stats: {
        clients: { current: 0, target: 500 },
        projects: { current: 0, target: 1000 },
        team: { current: 0, target: 50 },
        countries: { current: 0, target: 10 }
      },
      allProducts: allProducts, // 使用公共商品数据
      statsAnimated: false, // 标记统计数字是否已经动画过
      isSubmitting: false, // 表单提交状态
      showSuccessMessage: false // 成功消息显示状态

    };
  },
    mounted() {
    this.initNavbar();
    this.initScrollEffects();
    this.initSmokeParticles();
    this.initAboutSectionObserver();
    this.initContactSectionObserver();

    // 添加滚动事件监听，用于所有滚动效果
    window.addEventListener('scroll', this.handleScroll, { passive: false });

    // 启动跑马灯自动播放
    this.startAutoPlay();
  },
  beforeDestroy() {
    // 清理事件监听器和动画
    const canvas = document.getElementById('mist-canvas');
    if (canvas) {
      canvas.removeEventListener('mousemove', this.createParticle);
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // 清理Intersection Observer
    if (this.aboutSectionObserver) {
      this.aboutSectionObserver.disconnect();
    }
    if (this.contactSectionObserver) {
      this.contactSectionObserver.disconnect();
    }

    // 清理自动播放定时器
    this.stopAutoPlay();

    // 移除事件监听
    window.removeEventListener('scroll', this.handleScroll, { passive: false });
  },
  methods: {
    handleSubmit(event) {
      event.preventDefault();
      this.isSubmitting = true;

      const form = event.target;
      const formData = new FormData(form);

      // 使用fetch API发送表单数据到Formspree
      fetch(form.action, {
        method: form.method,
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (response.ok) {
          this.showSuccessMessage = true;
          form.reset();

          // 3秒后隐藏成功消息
          setTimeout(() => {
            this.showSuccessMessage = false;
          }, 3000);
        } else {
          return response.json().then(data => {
            if (data.errors) {
              // eslint-disable-next-line no-console
              console.error('Form submission errors:', data.errors);
              alert('There was an error submitting the form. Please try again.');
            }
          });
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Form submission error:', error);
        alert('There was a network error. Please try again later.');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      const navLinks = document.querySelector('.nav-links');
      const hamburger = document.querySelector('.hamburger');
      navLinks.classList.toggle('active');
      hamburger.classList.toggle('active');
    },
    // 滚动到指定区域
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    // 初始化滚动效果
    initScrollEffects() {
      // 初始化时检查是否需要执行动画
      this.handleScroll();
    },

    // 处理滚动事件
    handleScroll() {
      // 处理英雄区域动画
      const heroSection = document.querySelector('.hero');
      if (heroSection) {
        const heroContent = heroSection.querySelector('.hero-content');
        const rect = heroSection.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

        if (isVisible) {
          heroContent.classList.add('animate');
        } else {
          heroContent.classList.remove('animate');
        }
      }

      // 处理 BENEFITS 模块的滚动效果
      // 处理features区域的滚动类和缩放效果
      const featuresSection = document.querySelector('.features-transform');
      if (featuresSection) {
        // 到达features锚点的条件：区域顶部进入视口顶部
        const rect = featuresSection.getBoundingClientRect();
        const hasReachedFeaturesAnchor = rect.top <= 0;

        // 添加/移除滚动类
        featuresSection.classList.toggle('scrolled', hasReachedFeaturesAnchor);
      }

      // 处理统计数字动画的逻辑已移至initAboutSectionObserver方法中

      // 处理产品块反转动画
      this.animateProductBlocks();

      // 处理邮箱地址颜色渐变动画
      const emailText = document.querySelector('.email-text');
      if (emailText) {
        const rect = emailText.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

        if (isVisible) {
          emailText.classList.add('animate-gradient');
        }
      }

      // 处理关于我们文字的滚动动画 - 只处理about区域的元素
      const aboutFadeTexts = document.querySelectorAll('.about-content .fade-in-text');
      aboutFadeTexts.forEach((text) => {
        const rect = text.getBoundingClientRect();
        // 调整可见性判断条件，使其更容易触发
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

        if (isVisible) {
          text.classList.add('active');
        } else {
          // 当元素离开视口时，移除active类，确保下次进入时能重新触发动画
          text.classList.remove('active');
        }
      });
    },

    // 移除复杂的wheel事件处理，使用更简单的scroll事件实现

    // 动画产品块和证书从左到右依次反转
    animateProductBlocks() {
      // 处理Sopuria Products区域
      const sopuriaProducts = document.querySelectorAll('#products .product-item');
      this.animateBlockSequence(sopuriaProducts, 0);

      // 处理Kangertech Products区域，添加延迟使其在Sopuria区域动画完成后开始
      const kangertechProducts = document.querySelectorAll('#kangertech-products .product-item');
      // Sopuria Products动画总时长 = 产品数量 * 间隔时间
      const sopuriaAnimationDuration = sopuriaProducts.length * 100;
      this.animateBlockSequence(kangertechProducts, sopuriaAnimationDuration);

      // 移除证书翻转效果，保持证书始终可见
    },

    // 依次动画序列
    animateBlockSequence(blocks, startDelay) {
      blocks.forEach((block, index) => {
        // 检查元素是否在视口中
        const rect = block.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

        if (isVisible) {
          // 移除已有的动画类，以便重新播放动画
          block.classList.remove('flipped');

          // 从左到右依次延迟触发动画，添加开始延迟
          setTimeout(() => {
            block.classList.add('flipped');
          }, startDelay + (index * 100)); // 100ms间隔
        }
      });
    },


    // 动画显示统计数字跑马灯左右按钮切换失灵
    animateStats() {
      const duration = 2000; // 动画持续时间（毫秒）
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // 使用缓动函数使动画更自然
        const easeOutQuad = progress * (2 - progress);

        // 更新所有统计数字
        this.stats.clients.current = Math.floor(this.stats.clients.target * easeOutQuad);
        this.stats.projects.current = Math.floor(this.stats.projects.target * easeOutQuad);
        this.stats.team.current = Math.floor(this.stats.team.target * easeOutQuad);
        this.stats.countries.current = Math.floor(this.stats.countries.target * easeOutQuad);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      animate();
    },

    // 使用Intersection Observer监测about区域的可见性
    initAboutSectionObserver() {
      const aboutSection = document.getElementById('about');
      if (!aboutSection) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // 元素进入视口时触发动画
              if (!this.statsAnimated) {
                // 确保在调用animateStats前重置统计数字
                this.stats.clients.current = 0;
                this.stats.projects.current = 0;
                this.stats.team.current = 0;
                this.stats.countries.current = 0;
                // 调用动画函数
                this.animateStats();
                this.animateAboutText();
                this.statsAnimated = true;
              }
            } else {
              // 元素离开视口时重置动画状态
              this.statsAnimated = false;
              // 移除文字动画类，以便下次进入时重新触发
              const fadeTexts = document.querySelectorAll('.fade-in-text');
              fadeTexts.forEach(text => text.classList.remove('active'));
            }
          });
        },
        {
          threshold: 0.25, // 当元素的25%进入视口时触发
          rootMargin: '0px 0px -100px 0px' // 添加底部边距，使动画在元素稍微进入视口时就触发
        }
      );

      // 开始观察about区域
      observer.observe(aboutSection);

      // 保存observer实例，以便在组件销毁时清理
      this.aboutSectionObserver = observer;
    },
    initContactSectionObserver() {
      // 创建Intersection Observer实例来观察contact区域
      const contactSection = document.getElementById('contact');
      if (!contactSection) return;

      // 配置Intersection Observer，当元素25%可见时触发回调
      const observerOptions = {
        threshold: 0.1, // 降低阈值到10%，更容易触发
        rootMargin: '-50px 0px -50px 0px' // 调整触发区域
      };

      // 创建观察者实例
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // 元素进入视口时触发文字动画
              this.animateContactText();
            } else {
              // 元素离开视口时重置动画状态
              const fadeTexts = contactSection.querySelectorAll('.fade-in-text');
              fadeTexts.forEach(text => text.classList.remove('active'));
            }
          });
        },
        observerOptions
      );

      // 开始观察contact区域
      observer.observe(contactSection);

      // 保存observer实例，以便在组件销毁时清理
      this.contactSectionObserver = observer;
    },
    animateContactText() {
      // 处理联系我们文字的动画
      const contactSection = document.getElementById('contact');
      if (!contactSection) return;

      // 处理普通的淡入文字动画
      const fadeTexts = contactSection.querySelectorAll('.fade-in-text');
      fadeTexts.forEach((text) => {
        text.classList.add('active');
      });

      // 专门处理邮箱文字的渐变动画
      const emailText = contactSection.querySelector('.email-text');
      if (emailText) {
        emailText.classList.add('animate-gradient');
      }
    },
    animateAboutText() {
      // 处理关于我们文字的动画
      const fadeTexts = document.querySelectorAll('.fade-in-text');
      fadeTexts.forEach((text, index) => {
        // 使用setTimeout添加延迟，创造更自然的动画效果
        setTimeout(() => {
          text.classList.add('active');
        }, index * 300); // 每个文字块间隔300ms
      });
    },

    showHighRes(certificateType) {
      try {
        // 重置加载状态
        this.highResImageLoaded = false;
        // 显示高清大图容器
        this.showHighResImage = true;
        // 使用require来正确加载高清图片
        this.highResImageUrl = require(`@/assets/img/company-honor-${certificateType}.jpg`);
        // 先移除现有的事件监听器，避免重复添加
        document.removeEventListener('click', this.handleClickOutside);
        // 添加点击外部关闭弹窗的事件监听
        setTimeout(() => {
          document.addEventListener('click', this.handleClickOutside);
        }, 0);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载高清图片失败:', error);
        this.showHighResImage = false;
      }
    },
    hideHighRes() {
      // 隐藏高清大图并重置加载状态
      this.showHighResImage = false;
      this.highResImageLoaded = false;
      // 移除点击外部关闭弹窗的事件监听
      document.removeEventListener('click', this.handleClickOutside);
    },
    handleClickOutside(event) {
      // 获取弹窗元素
      const highResCertificate = document.querySelector('.high-res-certificate');
      // 如果点击的是弹窗外部，则关闭弹窗
      if (highResCertificate && !highResCertificate.contains(event.target)) {
        this.hideHighRes();
      }
    },

    // 跑马灯方法 - 滚动到指定幻灯片
    goToSlide(index) {
      // 确保索引有效
      this.currentSlide = Math.max(0, Math.min(index, this.marqueeImages.length - 1));
      this.updateMarqueePosition();

      // 用户交互后重置自动播放定时器
      this.resetAutoPlay();
    },

    // 跑马灯方法 - 上一张/下一张控制
    scrollMarquee(direction) {
      // 确保direction有效
      if (!direction) return;

      // 更新当前幻灯片索引
      if (direction === 'prev') {
        this.currentSlide = this.currentSlide === 0 ? this.marqueeImages.length - 1 : this.currentSlide - 1;
      } else if (direction === 'next') {
        this.currentSlide = this.currentSlide === this.marqueeImages.length - 1 ? 0 : this.currentSlide + 1;
      }

      this.updateMarqueePosition();

      // 用户交互后重置自动播放定时器
      this.resetAutoPlay();
    },

    // 启动自动播放
    startAutoPlay() {
      // 清除可能存在的旧定时器
      this.stopAutoPlay();

      // 创建新的自动播放定时器
      this.autoPlayTimer = setInterval(() => {
        this.scrollMarquee('next');
      }, this.autoPlayInterval);
    },

    // 停止自动播放
    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
    },

    // 重置自动播放（用户交互时调用）
    resetAutoPlay() {
      this.startAutoPlay();
    },

    // 更新跑马灯位置（统一实现）
    updateMarqueePosition() {
      const wrapper = this.$refs.marqueeWrapper;
      const container = document.querySelector('.marquee-container');

      if (wrapper && container) {
        const slideWidth = container.offsetWidth;
        wrapper.style.transition = 'transform 0.5s ease-in-out';
        wrapper.style.transform = `translateX(-${this.currentSlide * slideWidth}px)`;

        // 更新hero-text内容并添加过渡特效
        this.updateHeroText();
      }
    },

    // 更新hero-text内容
    updateHeroText() {
      // 添加过渡特效
      this.textTransition = true;

      // 短暂延迟后更新文本内容
      setTimeout(() => {
        const currentItem = this.marqueeImages[this.currentSlide];
        this.currentHeroText = {
          title: currentItem.title,
          description: currentItem.description
        };

        // 移除过渡特效类，为下一次切换做准备
        setTimeout(() => {
          this.textTransition = false;
        }, 500);
      }, 250);
    },

    // 移除自动滚动相关方法
    handleNavLinkClick(event) {
      // 检查点击的是否是导航链接
      if (event.target.tagName === 'A') {
        event.preventDefault(); // 阻止默认的跳转行为

        // 获取锚点链接的href属性
        const href = event.target.getAttribute('href');

        // 如果是锚点链接
        if (href.startsWith('#')) {
          // 提取目标锚点的ID
          const targetId = href.slice(1);

          // 根据ID获取目标元素
          const targetElement = document.getElementById(targetId);

          // 如果找到目标元素，则平滑滚动到该元素
          if (targetElement) {
            // 重置动画状态，使动画可以重新播放
            this.resetAnimations();

            // 平滑滚动到目标元素
            targetElement.scrollIntoView({
              behavior: 'smooth', // 平滑滚动
              block: 'start' // 滚动到元素的顶部
            });

            // 在滚动完成后重新触发动画
            setTimeout(() => {
              this.handleScroll();

              // 如果目标是features区域（跑马灯），重新触发文本动画
              if (targetId === 'features') {
                const heroText = document.querySelector('.hero-text');
                if (heroText) {
                  heroText.style.animation = 'fadeInUp 1.5s ease-out forwards';
                }
              }
            }, 1000); // 等待滚动完成后再触发动画
          }
        }

        // 如果菜单是打开状态，则关闭菜单
        if (this.isMenuOpen) {
          this.toggleMenu();
        }
      }
    },

    // 重置动画状态
    resetAnimations() {
      // 重置英雄区域动画
      const heroContent = document.querySelector('.hero-content');
      if (heroContent) {
        heroContent.classList.remove('animate');
        // 强制重排以确保动画可以重新触发
        void heroContent.offsetWidth;
      }

      // 重置跑马灯文本动画
      const heroText = document.querySelector('.hero-text');
      if (heroText) {
        heroText.style.animation = 'none';
        // 强制重排以确保动画可以重新触发
        void heroText.offsetWidth;
      }

      // 重置产品块动画
      const allProducts = document.querySelectorAll('.product-item');
      allProducts.forEach(product => {
        product.classList.remove('flipped');
      });

      // 重置证书动画
      const allCertificates = document.querySelectorAll('.certificate-item');
      allCertificates.forEach(certificate => {
        certificate.classList.remove('flipped');
      });

      // 重置统计数字动画
      this.statsAnimated = false;

      // 重置邮箱地址渐变动画
      const emailText = document.querySelector('.email-text');
      if (emailText) {
        emailText.classList.remove('animate-gradient');
      }

      // 重置关于我们文字动画
      const fadeTexts = document.querySelectorAll('.fade-in-text');
      fadeTexts.forEach(text => {
        text.classList.remove('active');
      });
    },
    initNavbar() {
      // 移除重复的事件监听器，使用Vue的@click事件绑定即可
    },
    // 初始化烟雾粒子效果
    initSmokeParticles() {
      // 为home区域初始化烟雾特效
      this.initCanvasEffect('mist-canvas', '.hero');
      // 初始化features区域的烟雾效果
      this.initCanvasEffect('features-mist-canvas', '.features-transform');
      // 监听窗口大小变化，调整canvas大小
      window.addEventListener('resize', () => {
        this.resizeCanvas('mist-canvas', '.hero');
        this.resizeCanvas('features-mist-canvas', '.features-transform');
      });

      // 开始动画循环
      this.animateParticles();
    },

    // 初始化单个canvas的烟雾特效
    initCanvasEffect(canvasId, targetSelector) {
      // 如果还没有particles数组，创建一个
      if (!this.particles) {
        this.particles = [];
      }

      // 如果还没有canvasContexts对象，创建一个
      if (!this.canvasContexts) {
        this.canvasContexts = {};
      }

      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      // 为每个画布创建独立的上下文
      if (!this.canvasContexts[canvasId]) {
        this.canvasContexts[canvasId] = canvas.getContext('2d');
      }

      // 设置canvas大小
      this.resizeCanvas(canvasId, targetSelector);

      // 添加鼠标移动事件监听器
      const target = document.querySelector(targetSelector);
      if (target) {
        target.addEventListener('mousemove', (e) => {
          this.createParticle(e, canvasId);
        });
      }
    },
    // 调整canvas大小
    resizeCanvas(canvasId = 'mist-canvas', targetSelector = '.hero') {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      const target = document.querySelector(targetSelector);
      if (target) {
        canvas.width = target.offsetWidth;
        canvas.height = target.offsetHeight;
      }
    },
    // 创建粒子
    createParticle(e, canvasId = 'mist-canvas') {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // 创建多个粒子（增加数量）
      for (let i = 0; i < 20; i++) {
        this.particles.push({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          size: Math.random() * 20 + 10, // 增加大小
          speedX: (Math.random() - 0.5) * 6, // 增加速度
          speedY: (Math.random() - 0.5) * 6 - 2,
          opacity: Math.random() * 1 + 0.5, // 增加透明度
          life: 1,
          decay: Math.random() * 0.02 + 0.005, // 延长生命周期
          canvasId: canvasId // 记录粒子属于哪个canvas
        });
      }
    },
    // 动画循环
    animateParticles() {
      // 先清除所有canvas
      const canvases = ['mist-canvas', 'features-mist-canvas'];
      canvases.forEach(canvasId => {
        const canvas = document.getElementById(canvasId);
        if (canvas && this.canvasContexts && this.canvasContexts[canvasId]) {
          const ctx = this.canvasContexts[canvasId];
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });

      for (let i = this.particles.length - 1; i >= 0; i--) {
        const particle = this.particles[i];

        // 更新粒子位置
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // 更新粒子透明度
        particle.life -= particle.decay;
        particle.opacity = particle.life * 0.8;

        // 获取粒子所属的canvas上下文
        if (this.canvasContexts && this.canvasContexts[particle.canvasId]) {
          const ctx = this.canvasContexts[particle.canvasId];
          // 绘制粒子（使用灰白色）
          ctx.fillStyle = `rgba(200, 200, 200, ${particle.opacity})`;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
        }

        // 移除死亡的粒子
        if (particle.life <= 0) {
          this.particles.splice(i, 1);
        }
      }

      // 继续动画循环
      this.animationFrameId = requestAnimationFrame(this.animateParticles);
    }
  }
};
</script>

<style lang="scss">
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 线条背景效果 */
@keyframes linesMove {
  0% { background-position: 0 0; }
  100% { background-position: 100% 100%; }
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* S曲线分割效果 */
@keyframes sCurvePulse {
  0%, 100% {
    clip-path: polygon(0 0, 100% 0, 100% calc(50% - 20px), 50% 50%, 0 calc(50% + 20px));
  }
  50% {
    clip-path: polygon(0 0, 100% 0, 100% calc(50% - 30px), 50% 50%, 0 calc(50% + 30px));
  }
}

html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  color: #fff;
  background-color: #121212;
}

.container {
  width: 100%;
  margin: 0;
  padding: 0 20px;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #ffffff;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #666666;
  }
}

.btn {
  display: inline-block;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background-color: #df6f45;
    color: white;

    &:hover {
      background-color: #e88a6a;
      transform: translateY(-2px);
    }
  }

  &.secondary {
    background-color: transparent;
    color: #666666;
    border: 2px solid #666666;

    &:hover {
      background-color: #666666;
      color: white;
      transform: translateY(-2px);
    }
  }

  &.small {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}

/* 导航栏样式 */
.navbar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background-color: transparent;
    z-index: 1000;
    transition: all 0.3s ease;

  .container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 20px;
      max-width: 1200px;
      margin: 1rem auto;
      background-color: rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    }

  .nav-left {
    flex: 1;
  }

  .nav-links {
    flex: 1;
    display: flex;
    list-style: none;
    gap: 2rem;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
  }

  .nav-actions {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
  }

  .hamburger {
    display: none;
    flex-direction: column;
    cursor: pointer;
    gap: 5px;
    padding: 10px;
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;

    span {
      width: 25px;
      height: 3px;
      background-color: white;
      border-radius: 2px;
      transition: all 0.3s ease;
    }

    &.active {
      span:nth-child(1) {
        transform: rotate(45deg) translate(6px, 6px);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:nth-child(3) {
        transform: rotate(-45deg) translate(6px, -6px);
      }
    }
  }

  .language-switch {
    .el-dropdown-link {
      display: inline-block;
      padding: 8px 24px;
      background-color: white;
      color: #1e1e1e;
      text-decoration: none;
      font-weight: 600;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-radius: 4px;
      transition: all 0.3s ease;

      &:hover {
        background-color: #f0f0f0;
        transform: translateY(-2px);
      }

      .el-icon {
        color: #1e1e1e;
      }
    }
  }

  .nav-links {
      display: flex;
      list-style: none;
      gap: 2rem;
      align-items: center;
      margin: 0;
      padding: 0;

      li {
        min-width: 120px; /* 增加最小宽度以容纳德语文本 */
        text-align: center;
      }

      li a {
        text-decoration: none;
        color: #fff;
        font-weight: 500;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: color 0.3s ease;

        &:hover {
          color: #666666;
        }
      }

      .logo {
        a {
          display: inline-block;
          padding: 4px 12px;
          background-color: white;
          color: #1e1e1e;
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: 1px;
          border-radius: 4px;
        }
      }
  }

  /* 语言切换按钮样式已在上方定义 */

  .hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    cursor: pointer;

    span {
      width: 25px;
      height: 3px;
      background-color: #fff;
      transition: all 0.3s ease;
    }

    &.active {
      span:first-child {
        transform: rotate(45deg) translateY(12px);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:last-child {
        transform: rotate(-45deg) translateY(-12px);
      }
    }
  }
}

/* 英雄区域样式 */
.hero {
  height: 100vh;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  #mist-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
  }

  .hero-content {
    text-align: center;
    z-index: 2;
    padding: 0 20px;

    h2 {
      font-size: 5.5rem;
      margin-bottom: 1.5rem;
      color: #fff;
      font-weight: 700;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      opacity: 0;
      transform: translateY(30px);
    }

    p {
      font-size: 2rem;
      margin-bottom: 2rem;
      color: #fff;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      opacity: 0;
      transform: translateY(30px);
    }

    .cta-buttons {
      display: flex;
      gap: 1rem;
      justify-content: center;
      flex-wrap: wrap;
      opacity: 0;
      transform: translateY(30px);
    }

    // 动画类
    &.animate {
      h2 {
        animation: fadeInUp 1s ease-out 0.3s forwards;
      }

      p {
        animation: fadeInUp 1s ease-out 0.6s forwards;
      }

      .cta-buttons {
        animation: fadeInUp 1s ease-out 0.9s forwards;
      }
    }
  }

  .hero-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 0; /* 确保图片显示在背景层 */

    .hero-bg {
      width: 100%;
      height: 100%;
      background-image: url('./assets/img/home-1.png');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      opacity: 0.8;
    }
  }

  /* 小屏幕响应式设计 */
  @media (max-width: 480px) {
    .hero-image .hero-bg {
      background-position: right 10% center; /* 让图片稍微向右移动 */
    }
  }
}

/* 功能介绍样式 - 改造后 */
.features-transform {
    position: relative;
    height: 100vh; /* 固定全屏高度 */
    background-color: #121212;
    overflow: hidden; /* 隐藏溢出内容 */
  z-index: 1;

  .features-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
  }

  /* 初始大图和文字 */
  .hero-section {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 90%;
    height: 100vh;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 1; /* 显示文本 */
    margin: 0 auto;
  }

  .hero-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('./assets/img/home-1.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: brightness(0.7);
    opacity: 1 !important;
    transform-origin: center;
    z-index: 1; /* 降低图片层级，确保烟雾特效显示在图片上方 */
  }

  .hero-text {
    position: relative;
    text-align: center;
    color: white;
    z-index: 4; /* 提高z-index值，确保文本显示在雾气特效上方 */
    padding: 0 80px; /* 增加左右内边距，为箭头按钮留出空间 */
    max-width: 80%; /* 限制最大宽度，避免覆盖按钮 */
    animation: fadeInUp 1.5s ease-out forwards;
  }

  /* 淡入上移动画 */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-text h1 {
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .hero-text p {
    font-size: 1.2rem;
    max-width: 600px;
    margin: 0 auto;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  /* 文本过渡特效 */
  .hero-text .text-transition {
    opacity: 0;
    transform: translateY(20px);
  }

  /* 图片网格 */
  .images-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-areas:
      ". top-left top-right ."
      "left center center right"
      ". bottom-left bottom-right .";
    grid-template-columns: 1fr 300px 300px 1fr;
    grid-template-rows: 1fr 300px 1fr;
    gap: 20px;
    align-items: center;
    justify-items: center;
    opacity: 1 !important;
    z-index: 1;
  }

  /* 跑马灯样式 */
  .marquee-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 2; /* 确保跑马灯显示在最上层 */
  }

  .marquee-wrapper {
    display: flex;
    width: 100%;
    height: 100%;
    transition: transform 0.5s ease-in-out;
  }

  .marquee-slide {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
  }

  .marquee-image {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }



  /* 响应式调整 */
  @media (max-width: 1200px) {
    /* 调整hero-text在中等屏幕上的字体大小 */
    .hero-text h1 {
      font-size: 3rem;
    }

    .hero-text p {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 992px) {
    /* 调整hero-text在小屏幕上的字体大小 */
    .hero-text h1 {
      font-size: 2.5rem;
    }

    .hero-text p {
      font-size: 1rem;
    }

    /* 调整按钮大小 */
    .marquee-btn {
      width: 45px;
      height: 45px;
      font-size: 1.3rem;
    }

    /* 调整文本内边距 */
    .hero-text {
      padding: 0 60px;
    }
  }

  @media (max-width: 768px) {
    /* 调整hero-text在平板上的字体大小 */
    .hero-text h1 {
      font-size: 2rem;
    }

    .hero-text p {
      font-size: 0.9rem;
      max-width: 90%;
    }

    /* 调整按钮大小和位置 */
    .marquee-btn {
      width: 40px;
      height: 40px;
      font-size: 1.2rem;
    }

    .marquee-btn.prev {
      left: 15px;
    }

    .marquee-btn.next {
      right: 15px;
    }



    /* 调整文本内边距 */
    .hero-text {
      padding: 0 40px;
      max-width: 90%;
    }

    /* 调整hero-section宽度 */
    .hero-section {
      width: 95%;
    }
  }

  @media (max-width: 576px) {
    /* 调整hero-text在手机上的字体大小 */
    .hero-text h1 {
      font-size: 1.8rem;
      letter-spacing: 1px;
    }

    .hero-text p {
      font-size: 0.85rem;
      max-width: 100%;
    }

    /* 调整按钮大小和位置 */
    .marquee-btn {
      width: 35px;
      height: 35px;
      font-size: 1.1rem;
    }

    .marquee-btn.prev {
      left: 10px;
    }

    .marquee-btn.next {
      right: 10px;
    }



    /* 调整文本内边距 */
    .hero-text {
      padding: 0 20px;
      max-width: 100%;
    }

    /* 调整hero-section宽度 */
    .hero-section {
      width: 100%;
    }
  }

  /* 控制按钮 */
  .marquee-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background-color: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 100; /* 提高z-index确保按钮在最上层 */
    outline: none;
    user-select: none;
  }

  .marquee-btn:hover {
    background-color: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }

  .marquee-btn.prev {
    left: 20px;
  }

  .marquee-btn.next {
    right: 20px;
  }

  /* 指示器 - 隐藏 */
  .marquee-indicators {
    display: none;
  }

  .indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .indicator.active {
    background-color: white;
    transform: scale(1.2);
  }
}

/* 产品展示样式 - 完全分离 */

/* Sopuria Products - 独立样式 */
#products {
  padding: 8rem 0;
  background-color: #181818;
  background-repeat: no-repeat;
  /* 增强的线条纹理 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  /* 更明显的S曲线分割 - 上半部分 */
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - 80px), 50% 100%, 0 calc(100% - 80px));
  /* 线条移动动画 */
  animation: linesMove 15s linear infinite;
  /* 为下半部分准备的伪元素 */
  position: relative;
}

#products::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 80px;
  /* 稍微不同的背景色增强对比 */
  background-color: #181818;
  /* 相同的线条纹理 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  background-position: 0 0;
  /* 更明显的S曲线 */
  clip-path: polygon(0 0, 50% 100%, 100% 0);
  /* 添加发光边框增强S曲线 */
  border-top: 1px solid rgba(255,255,255,0.15);
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  animation: linesMove 15s linear infinite;
  z-index: 1;
}

/* Kangertech Products - 独立样式 */
#kangertech-products {
  background-color: #222222;
  background-repeat: no-repeat;
  /* 增强的线条纹理 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  /* 调整位置以与上部分的S曲线对接 */
  padding: 80px 0 8rem;
  /* 线条移动动画 */
  animation: linesMove 15s linear infinite;
  position: relative;
  z-index: 2;
}

  /* Sopuria Products - 4 columns - 产品内容完全独立样式 */
  #products .products-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    max-width: 100%;
    width: 100%;
    margin: 0;
    padding: 0 1rem;
    box-sizing: border-box;
  }

  /* 响应式布局 - Sopuria Products */
  @media (max-width: 1200px) {
    #products .products-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1.8rem;
    }
  }

  @media (max-width: 992px) {
    #products .products-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1.2rem;
    }
  }

  @media (max-width: 768px) {
    #products .products-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
    }

    #products .product-image {
      height: 100px;
    }

    #products .product-info {
      padding: 0.8rem;
    }

    #products .product-info h3 {
      font-size: 0.9rem;
    }

    #products .product-info p {
      font-size: 0.7rem;
      height: 30px;
    }
  }

  @media (max-width: 576px) {
    #products .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.8rem;
    }

    #products .product-image {
      height: 90px;
    }
  }

  @media (max-width: 400px) {
    #products .products-grid {
      grid-template-columns: 1fr;
    }

    #products .product-image {
      height: 150px;
    }
  }

  /* 添加梦幻效果的关键帧动画 */
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes lightSweep {
    0% { transform: translateX(-100%) translateY(-50%) rotate(15deg); }
    100% { transform: translateX(300%) translateY(-50%) rotate(15deg); }
  }

  #products .product-item {
    background: linear-gradient(135deg, rgba(18,18,18,0.8) 0%, rgba(20,20,20,0.9) 50%, rgba(24,24,24,0.8) 100%);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 0 0 20px rgba(223, 111, 69, 0.15);
    transition: all 0.3s ease;
    opacity: 1;
    transform: translateY(0);
    position: relative;
    border: 1px solid rgba(223, 111, 69, 0.3);
  }

  /* 动画完成状态 */
  #products .product-item.flipped {
    opacity: 1;
    transform: translateY(0);
  }

  #products .product-item:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5), 0 0 40px rgba(223, 111, 69, 0.3);
  }

  #products .product-item::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(223, 111, 69, 0.2), transparent);
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
    z-index: 1;
  }

  #products .product-image {
    height: 250px;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }

  #products .product-bg {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: all 0.3s ease;
    filter: brightness(1.1) contrast(1.05);
  }

  #products .product-item:hover .product-bg {
    transform: scale(1.05);
    filter: brightness(1.2) contrast(1.1);
  }

  #products .product-image::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(223, 111, 69, 0.1) 50%, transparent 100%);
    z-index: 2;
    animation: lightSweep 4s infinite ease-in-out;
  }

  #products .product-image::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid transparent;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(223, 111, 69, 0.4), rgba(100, 200, 255, 0.4)) border-box;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    z-index: 3;
  }

  #products .product-info {
    padding: 1.2rem;
    text-align: center;
    position: relative;
    z-index: 2;
  }

  #products .product-info h3 {
    font-size: 1rem;
    margin-bottom: 0.3rem;
    color: #fff;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 0 10px rgba(223, 111, 69, 0.5), 0 0 20px rgba(223, 111, 69, 0.3);
    transition: all 0.3s ease;
  }

  #products .product-item:hover .product-info h3 {
    text-shadow: 0 0 15px rgba(223, 111, 69, 0.8), 0 0 30px rgba(223, 111, 69, 0.5);
  }

  #products .product-info p {
    color: #b0b0b0;
    margin-bottom: 1rem;
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    height: 1.5em;
    line-height: 1.5;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;
  }

  #products .product-item:hover .product-info p {
    color: #e0e0e0;
  }

  #products .product-info .btn {
    background-color: #df6f45;
    color: white;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background-color 0.3s ease;
  }

  #products .product-info .btn:hover {
    background-color: #d65d32;
  }

  /* Kangertech Products - 5 columns - 产品内容完全独立样式 */
  #kangertech-products .products-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 2rem;
    max-width: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  /* 响应式布局 - Kangertech Products */
  @media (max-width: 1200px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1.8rem;
    }
  }

  @media (max-width: 1024px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }
  }

  @media (max-width: 768px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.2rem;
    }

    #kangertech-products .product-image {
      height: 180px;
    }
  }

  @media (max-width: 576px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }

    #kangertech-products .product-image {
      height: 150px;
    }
  }

  @media (max-width: 400px) {
    #kangertech-products .products-grid {
      grid-template-columns: 1fr;
    }

    #kangertech-products .product-image {
      height: 200px;
    }
  }

  #kangertech-products .product-item {
    background: linear-gradient(135deg, rgba(18,18,18,0.8) 0%, rgba(20,20,20,0.9) 50%, rgba(24,24,24,0.8) 100%);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 0 0 20px rgba(223, 111, 69, 0.15);
    transition: all 0.3s ease;
    opacity: 1;
    transform: translateY(0);
    position: relative;
    border: 1px solid rgba(223, 111, 69, 0.3);
  }

  /* 动画完成状态 */
  #kangertech-products .product-item.flipped {
    opacity: 1;
    transform: translateY(0);
  }

  #kangertech-products .product-item:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5), 0 0 40px rgba(223, 111, 69, 0.3);
  }

  #kangertech-products .product-item::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(223, 111, 69, 0.2), transparent);
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
    z-index: 1;
  }

  #kangertech-products .product-image {
    height: 200px;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }

  #kangertech-products .product-bg {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: all 0.3s ease;
    filter: brightness(1.1) contrast(1.05);
  }

  #kangertech-products .product-item:hover .product-bg {
    transform: scale(1.05);
    filter: brightness(1.2) contrast(1.1);
  }

  #kangertech-products .product-image::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(223, 111, 69, 0.1) 50%, transparent 100%);
    z-index: 2;
    animation: lightSweep 4s infinite ease-in-out;
  }

  #kangertech-products .product-image::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid transparent;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(223, 111, 69, 0.4), rgba(100, 200, 255, 0.4)) border-box;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    z-index: 3;
  }

  #kangertech-products .product-info {
    padding: 1.5rem;
    text-align: center;
    position: relative;
    z-index: 2;
  }

  #kangertech-products .product-info h3 {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    color: #fff;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(223, 111, 69, 0.5), 0 0 20px rgba(223, 111, 69, 0.3);
    transition: all 0.3s ease;
  }

  #kangertech-products .product-item:hover .product-info h3 {
    text-shadow: 0 0 15px rgba(223, 111, 69, 0.8), 0 0 30px rgba(223, 111, 69, 0.5);
  }

  #kangertech-products .product-info p {
    color: #b0b0b0;
    margin-bottom: 1.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    height: 1.5em;
    line-height: 1.5;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;
  }

  #kangertech-products .product-item:hover .product-info p {
    color: #e0e0e0;
  }

  #kangertech-products .product-info .btn {
    background-color: #df6f45;
    color: white;
    border: none;
    padding: 0.5rem 1.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background-color 0.3s ease;
  }

  #kangertech-products .product-info .btn:hover {
    background-color: #d65d32;
  }

/* 关于我们样式 */
.about {
  padding: 8rem 0;
  background-color: #121212;

  .about-content {
        text-align: center;
        margin: 0 20px 3rem;

        p {
          font-size: 1.1rem;
          color: #b0b0b0;
          margin-bottom: 1.5rem;
          line-height: 1.7;
        }

        /* 增加选择器优先级 */
        .fade-in-text {
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .fade-in-text.active {
          animation: fadeInUp 1s ease-out forwards;
        }

        .about-content .fade-in-text.active:first-child {
          animation-delay: 0.3s;
        }

        .about-content .fade-in-text.active:last-child {
          animation-delay: 0.6s;
        }
      }

  .about-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 2rem;
    text-align: center;

    .stat-item {
        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #df6f45;
          text-shadow: 0 0 10px rgba(253, 135, 86, 0.5);
        }

        .stat-label {
          display: block;
          margin-top: 0.5rem;
          color: #b0b0b0;
          font-weight: 500;
        }
      }
  }
}

/* 证书模块样式 */
.certificates {
  padding: 8rem 0;
  background-color: #121212;

  .section-title {
    text-align: center;
    margin-bottom: 1rem;
  }

  .section-subtitle {
    text-align: center;
    color: #b0b0b0;
    margin-bottom: 4rem;
    padding: 0 20px;
  }

  .certificates-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
      margin: 0 10px;
    }

    .certificate-item {
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.3s ease;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      position: relative;
      cursor: pointer;
      opacity: 1;
      transform: translateY(0deg);
      transition: transform 0.3s ease, opacity 0.3s ease;

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);

      .certificate-info {
        opacity: 1;
      }
    }

    &.flipped {
      opacity: 1;
      transform: translateY(0deg);
    }

    .certificate-image {
      width: 100%;
      height: 250px;
      overflow: hidden;
      background-color: rgba(255, 255, 255, 0.08);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .certificate-placeholder {
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        opacity: 0.7;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 3rem;
        color: white;
      }

      .certificate-placeholder::before {
        content: "📄";
      }
    }

    .certificate-info {
      padding: 1.5rem;
      text-align: center;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(0, 0, 0, 0.8);
      opacity: 0;
      transition: opacity 0.3s ease;

      h3 {
        color: white;
        font-size: 1.2rem;
        margin-bottom: 0.5rem;
        font-weight: 600;
      }

      p {
        color: #b0b0b0;
        font-size: 0.9rem;
        margin: 0;
      }
    }
  }

  /* 移动端媒体查询 - 取消反转效果 */
  @media (max-width: 768px) {
    .certificate-item {
      opacity: 1;
      transform: translateY(0) !important;
      transition: transform 0.3s ease, opacity 0.3s ease;
    }

    .certificate-item:hover {
      transform: translateY(-10px) !important;
    }

    .certificate-item.flipped {
      opacity: 1;
      transform: translateY(0) !important;
    }
  }

  /* 小屏幕媒体查询 - 直接显示证书信息 */
  @media (max-width: 480px) {
    .certificate-item .certificate-info {
      opacity: 1; /* 直接显示证书信息，不需要鼠标悬浮 */
      position: relative; /* 改为相对定位，避免覆盖图片 */
      background: rgba(0, 0, 0, 0.6); /* 降低背景透明度，使图片更清晰 */
    }

    /* 调整证书图片高度，确保信息有足够空间显示 */
    .certificate-item .certificate-image {
      height: 200px;
    }
  }
}

/* 高清证书图片展示样式 */
.high-res-certificate {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    display: block;
    max-width: 100%;
    max-height: 80vh;
    margin: 0 auto;
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  img[src] {
    opacity: 1;
  }
}

/* 图片加载指示器 */
.image-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(223, 111, 69, 0.3);
  border-top-color: #df6f45;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 添加背景遮罩效果 */
.high-res-certificate::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1;
}

/* 联系我们样式 */
.contact {
  padding: 8rem 0;
  background-color: #1e1e1e;

  .contact-form {
    margin: 0 20px;

    .email-display {
      text-align: center;
      margin-bottom: 3rem;
      position: relative;
      overflow: hidden;
      display: inline-block;
      left: 50%;
      transform: translateX(-50%);
      transition: transform 0.3s ease;

      &:hover {
        transform: translateX(-50%) translateY(-10px);
      }

      .email-subtitle {
        font-size: 1rem;
        color: #666;
        margin-bottom: 1rem;
        opacity: 0.8;
      }

      .email-text {
        font-size: 7.5rem;
        font-weight: bold;
        font-family: 'Arial', 'Helvetica', sans-serif;
        color: white;
        position: relative;
        z-index: 1;
        background: linear-gradient(to right, #df6f45 50%, white 50%);
        background-size: 200% 100%;
        background-position: 100% 0;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        transition: background-position 2s ease;
        margin: 0;
      }

      /* Responsive adjustments for email text */
      @media (max-width: 1200px) {
        .email-text {
          font-size: 6rem;
        }
      }

      @media (max-width: 992px) {
        .email-text {
          font-size: 4.5rem;
        }
      }

      @media (max-width: 768px) {
        .email-text {
          font-size: 3rem;
        }
      }

      @media (max-width: 576px) {
        .email-text {
          font-size: 2rem;
        }
      }

      &:hover .email-text,
      .email-text.animate-gradient {
        background-position: 0 0;
      }
    }

    .divider {
      height: 1px;
      background-color: #333;
      margin: 3rem 0;
      width: 100%;
    }

    .contact-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 3rem;
      font-size: 0.8rem;
      color: #666;
      opacity: 0.7;
    }

    .contact-info-left,
    .contact-info-center,
    .contact-info-right {
      flex: 1;
    }

    .contact-info-left {
      text-align: left;
    }

    .contact-info-center {
      text-align: center;
    }

    .contact-info-right {
      text-align: right;
    }

    .contact-info p {
      margin: 0.3rem 0;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      max-width: 600px;
      margin: 0 auto;
      width: 100%;

      .form-group {
        input,
        textarea {
          width: 100%;
          padding: 12px 16px;
          font-size: 1rem;
          border: 1px solid #333;
          border-radius: 4px;
          background-color: #121212;
          color: #fff;
          transition: border-color 0.3s ease;

          &:focus {
            outline: none;
            border-color: #666666;
          }
        }

        textarea {
          resize: vertical;
        }
      }
    }
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .navbar {
    .nav-links {
      position: fixed;
      top: 70px;
      left: -100%;
      width: 100%;
      height: calc(100vh - 70px);
      background-color: #1e1e1e;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 2rem;
      transition: left 0.3s ease;

      &.active {
        left: 0;
      }
    }

    .hamburger {
      display: flex;
    }
  }
}

/* 功能介绍区域的烟雾画布样式 - 全局生效 */
.features-transform #features-mist-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3; /* 使用z-index:3，确保雾气特效显示在图片(z-index:2)上方 */
  pointer-events: none; /* 允许鼠标事件穿过画布 */
}

  .hero {
    height: 100vh;
    padding: 0;
    margin-top: 0;

    .hero-content {
      h2 {
        font-size: 5.5rem;
      }

      p {
        font-size: 2rem;
      }

      .cta-buttons {
        flex-direction: row;
        align-items: center;
      }
    }

    .hero-image {
      display: block;
    }
  }

  .section-title {
    font-size: 2rem;
  }

  .features {
    padding: 4rem 0;
  }

  .products {
    padding: 4rem 0;
  }

  .about {
    padding: 4rem 0;
  }

  .contact {
    padding: 4rem 0;
  }

  /* .footer {
    padding: 3rem 0 1.5rem;
  } */


@media (max-width: 480px) {
  .navbar {
    .logo h1 {
      font-size: 1.5rem;
    }
  }

  .hero {
    .hero-content {
      h2 {
        font-size: 2rem;
      }
    }
  }

  .section-title {
    font-size: 1.8rem;
  }
}

/* ProductCenter Overlay Styles */
.product-center-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 1000;
  overflow-y: auto;
  animation: fadeIn 0.5s ease-in-out;
}

.close-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  font-size: 30px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  transition: background-color 0.3s ease;
}

.close-btn:hover {
  background-color: #555;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Contact form success message */
#contact .success-message {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.5);
  border-radius: 8px;
  color: #4caf50;
  text-align: center;
  font-size: 1rem;
  font-weight: 500;
  animation: fadeIn 0.5s ease-in;
}

/* WhatsApp floating button */
.whatsapp-float {
  position: fixed;
  bottom: 40px;
  right: 20px;
  width: 70px;
  height: 70px;
  background-color: #25d366;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.3), 0 0 0 4px rgba(37, 211, 102, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* 响应式设计：小屏幕上调整右边距 */
  @media (max-width: 480px) {
    right: 5px;
  }
  text-decoration: none;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.whatsapp-float::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #25d366, #128c7e);
  border-radius: 50%;
  z-index: -1;
  transition: opacity 0.3s ease;
  opacity: 0;
}

.whatsapp-float:hover {
  transform: translateY(-8px) scale(1.05);
  box-shadow: 0 10px 30px rgba(37, 211, 102, 0.4), 0 0 0 8px rgba(37, 211, 102, 0.15);
}

.whatsapp-float:hover::before {
  opacity: 1;
}

.whatsapp-icon {
  width: 38px;
  height: 38px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.whatsapp-float:hover .whatsapp-icon {
  transform: rotate(5deg) scale(1.1);
}

/* 点击效果 */
.whatsapp-float:active {
  transform: translateY(-4px) scale(0.95);
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.5);
}

.whatsapp-float:active .whatsapp-icon {
  transform: rotate(0deg) scale(0.95);
}
</style>
